// src/auth/UniversityHeadEmailVerification.js

import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Button,
    Typography,
    Paper,
    useTheme,
    useMediaQuery,
    CircularProgress,
    Alert,
    LinearProgress,
    TextField
} from '@mui/material';
import {
    CheckCircle,
    ErrorOutlineOutlined,
    Send,
    ArrowBack,
    AccountBalance as InstitutionHeadIcon
} from '@mui/icons-material';
import { styled, alpha } from '@mui/material/styles';
import { useNavigate, useParams } from 'react-router-dom';
import useCustomToast from '../hooks/useToast';
import { motion } from 'framer-motion';
import axios from 'axios';
import { 
    APIInstitutionHeadResendVerification, 
    APIInstitutionHeadVerifyEmail 
} from '../apiServices/APIs';

const StyledPaper = styled(Paper)(({ theme }) => ({
    position: 'relative',
    zIndex: 2,
    width: '100%',
    maxWidth: 500,
    padding: theme.spacing(4),
    borderRadius: theme.spacing(3),
    backgroundColor: alpha(theme.palette.background.paper, 0.95),
    backdropFilter: 'blur(12px)',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    border: `1px solid ${alpha(theme.palette.common.white, 0.3)}`,
}));

const UniversityHeadEmailVerification = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const showToast = useCustomToast();
    const { token } = useParams();
    
    // Ref to prevent double-calling in Strict Mode
    const verificationStarted = useRef(false);

    const [verifying, setVerifying] = useState(true);
    const [verificationStatus, setVerificationStatus] = useState(null); // 'success', 'error', 'already_verified'
    const [countdown, setCountdown] = useState(5);
    const [resendEmail, setResendEmail] = useState('');
    const [resending, setResending] = useState(false);
    const [showResendForm, setShowResendForm] = useState(false);

    useEffect(() => {
        // Only run if we haven't started verification yet
        if (token && !verificationStarted.current) {
            verificationStarted.current = true;
            verifyEmail();
        } else if (!token) {
            navigate('/login');
        }
    }, [token]);

    useEffect(() => {
        if (verificationStatus === 'success' || verificationStatus === 'already_verified') {
            const timer = setInterval(() => {
                setCountdown((prev) => {
                    if (prev <= 1) {
                        clearInterval(timer);
                        navigate('/login');
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
            return () => clearInterval(timer);
        }
    }, [verificationStatus, navigate]);

    const verifyEmail = async () => {
        try {
            const response = await axios.get(`${APIInstitutionHeadVerifyEmail}/${token}`);
            const { status, message, verified } = response.data;

            if (status === 'success' && verified === 'yes') {
                setVerificationStatus('success');
                showToast('Success', message, 'success');
            } else if (status === 'success' && verified === 'already') {
                setVerificationStatus('already_verified');
                showToast('Info', message, 'info');
            } else if (status === 'success') {
                setVerificationStatus('success');
                showToast('Success', message || 'Email verified successfully!', 'success');
            } else {
                setVerificationStatus('error');
                showToast('Error', message || 'Verification failed', 'error');
            }
        } catch (error) {
            const errorData = error.response?.data;
            // Handle expired token logic
            if (errorData?.data?.can_resend) {
                setShowResendForm(true);
                setResendEmail(errorData?.data?.email || '');
            }
            setVerificationStatus('error');
            showToast('Error', errorData?.message || 'Verification link is invalid or expired', 'error');
        } finally {
            setVerifying(false);
        }
    };

    const handleResendVerification = async () => {
        if (!resendEmail) {
            showToast('Error', 'Please enter your email address', 'error');
            return;
        }

        setResending(true);
        try {
            const response = await axios.post(APIInstitutionHeadResendVerification, {
                email: resendEmail
            });

            if (response.data.status === 'success') {
                showToast('Success', response.data.message, 'success');
                setShowResendForm(false);
                setResendEmail('');
                setVerificationStatus('already_verified');
            } else {
                showToast('Error', response.data.message, 'error');
            }
        } catch (error) {
            console.error('Resend error:', error);
            showToast('Error', error.response?.data?.message || 'Failed to resend verification', 'error');
        } finally {
            setResending(false);
        }
    };

    if (verifying) {
        return (
            <Box sx={{ 
                display: 'flex', 
                minHeight: '100vh', 
                alignItems: 'center', 
                justifyContent: 'center', 
                background: `${theme.palette.common.auth}` 
            }}>
                <Box sx={{ textAlign: 'center' }}>
                    <CircularProgress sx={{ color: 'white' }} />
                    <Typography sx={{ mt: 2, color: 'white' }}>Verifying your email...</Typography>
                </Box>
            </Box>
        );
    }

    // Render success state
    if (verificationStatus === 'success') {
        return (
            <Box sx={{
                display: 'flex',
                minHeight: '100vh',
                position: 'relative',
                overflow: 'hidden',
                alignItems: 'center',
                justifyContent: 'center',
                p: isMobile ? 2 : 3,
                background: `${theme.palette.common.auth}`
            }}>
                <motion.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.5 }}
                >
                    <StyledPaper elevation={0}>
                        <Box sx={{ textAlign: 'center' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                                <InstitutionHeadIcon 
                                    sx={{ 
                                        fontSize: 64, 
                                        color: theme.palette.warning.main,
                                        bgcolor: alpha(theme.palette.warning.main, 0.1),
                                        borderRadius: '50%',
                                        p: 1.5
                                    }} 
                                />
                            </Box>
                            <CheckCircle sx={{ fontSize: 64, color: '#4caf50', mb: 2 }} />
                            <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                                Email Verified Successfully!
                            </Typography>
                            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                                Your email has been verified and your Institutional Research Lead account is now active.
                            </Typography>
                            <Alert severity="success" sx={{ mb: 3 }}>
                                You can now log in to access all leadership features of your Institutional Research Lead account.
                            </Alert>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Redirecting to login in {countdown} seconds...
                            </Typography>
                            <LinearProgress 
                                variant="determinate" 
                                value={(countdown / 5) * 100} 
                                sx={{ 
                                    mb: 2, 
                                    height: 6, 
                                    borderRadius: 3,
                                    '& .MuiLinearProgress-bar': {
                                        background: `linear-gradient(90deg, ${theme.palette.warning.main}, ${theme.palette.warning.dark})`,
                                    }
                                }}
                            />
                            <Button
                                fullWidth
                                variant="contained"
                                onClick={() => navigate('/login')}
                                sx={{
                                    py: 1.5,
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    fontWeight: 'bold',
                                    background: `linear-gradient(135deg, ${theme.palette.warning.main} 0%, ${theme.palette.warning.dark} 100%)`,
                                    '&:hover': {
                                        background: `linear-gradient(135deg, ${theme.palette.warning.dark} 0%, ${theme.palette.warning.darker || '#B45309'} 100%)`,
                                    }
                                }}
                            >
                                Go to Login Now
                            </Button>
                        </Box>
                    </StyledPaper>
                </motion.div>
            </Box>
        );
    }

    // Render already verified state
    if (verificationStatus === 'already_verified') {
        return (
            <Box sx={{
                display: 'flex',
                minHeight: '100vh',
                position: 'relative',
                overflow: 'hidden',
                alignItems: 'center',
                justifyContent: 'center',
                p: isMobile ? 2 : 3,
                background: `${theme.palette.common.auth}`
            }}>
                <motion.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.5 }}
                >
                    <StyledPaper elevation={0}>
                        <Box sx={{ textAlign: 'center' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                                <InstitutionHeadIcon 
                                    sx={{ 
                                        fontSize: 48, 
                                        color: theme.palette.warning.main,
                                        bgcolor: alpha(theme.palette.warning.main, 0.1),
                                        borderRadius: '50%',
                                        p: 1.5
                                    }} 
                                />
                            </Box>
                            <CheckCircle sx={{ fontSize: 64, color: '#4caf50', mb: 2 }} />
                            <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                                Email Already Verified
                            </Typography>
                            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                                Your email has already been verified. Your Institutional Research Lead account is active.
                            </Typography>
                            <Alert severity="info" sx={{ mb: 3 }}>
                                You can proceed to login and start using your Institutional Research Lead leadership features.
                            </Alert>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Redirecting to login in {countdown} seconds...
                            </Typography>
                            <LinearProgress 
                                variant="determinate"
                                value={(countdown / 5) * 100}
                                sx={{ 
                                    mb: 2, 
                                    height: 6, 
                                    borderRadius: 3,
                                    '& .MuiLinearProgress-bar': {
                                        background: `linear-gradient(90deg, ${theme.palette.warning.main}, ${theme.palette.warning.dark})`,
                                    }
                                }}
                            />
                            <Button
                                fullWidth
                                variant="contained"
                                onClick={() => navigate('/login')}
                                sx={{
                                    py: 1.5,
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    fontWeight: 'bold',
                                    background: `linear-gradient(135deg, ${theme.palette.warning.main} 0%, ${theme.palette.warning.dark} 100%)`,
                                    '&:hover': {
                                        background: `linear-gradient(135deg, ${theme.palette.warning.dark} 0%, ${theme.palette.warning.darker || '#B45309'} 100%)`,
                                    }
                                }}
                            >
                                Go to Login Now
                            </Button>
                        </Box>
                    </StyledPaper>
                </motion.div>
            </Box>
        );
    }

    // Render error state
    if (verificationStatus === 'error') {
        return (
            <Box sx={{
                display: 'flex',
                minHeight: '100vh',
                position: 'relative',
                overflow: 'hidden',
                alignItems: 'center',
                justifyContent: 'center',
                p: isMobile ? 2 : 3,
                background: `${theme.palette.common.auth}`
            }}>
                <motion.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.5 }}
                >
                    <StyledPaper elevation={0}>
                        <Box sx={{ textAlign: 'center' }}>
                            <ErrorOutlineOutlined sx={{ fontSize: 64, color: '#f44336', mb: 2 }} />
                            <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
                                Verification Failed
                            </Typography>
                            <Alert severity="error" sx={{ mb: 3 }}>
                                The verification link is invalid or has expired.
                            </Alert>
                            {!showResendForm ? (
                                <>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                        This could be because:
                                        <br />• The link has already been used
                                        <br />• The link has expired (valid for 24 hours)
                                        <br />• The link was copied incorrectly
                                    </Typography>
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        onClick={() => setShowResendForm(true)}
                                        sx={{
                                            py: 1.5,
                                            mb: 2,
                                            borderRadius: 2,
                                            textTransform: 'none',
                                            background: `linear-gradient(135deg, ${theme.palette.warning.main} 0%, ${theme.palette.warning.dark} 100%)`,
                                            '&:hover': {
                                                background: `linear-gradient(135deg, ${theme.palette.warning.dark} 0%, ${theme.palette.warning.darker || '#B45309'} 100%)`,
                                            }
                                        }}
                                    >
                                        Request New Verification Link
                                    </Button>
                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        onClick={() => navigate('/login')}
                                        startIcon={<ArrowBack />}
                                        sx={{
                                            py: 1.5,
                                            borderRadius: 2,
                                            textTransform: 'none',
                                            borderColor: theme.palette.warning.main,
                                            color: theme.palette.warning.main,
                                            '&:hover': {
                                                borderColor: theme.palette.warning.dark,
                                                backgroundColor: alpha(theme.palette.warning.main, 0.1),
                                            }
                                        }}
                                    >
                                        Back to Login
                                    </Button>
                                </>
                            ) : (
                                <Box sx={{ textAlign: 'left' }}>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                        Enter your email address to receive a new verification link:
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        label="Email Address"
                                        type="email"
                                        value={resendEmail}
                                        onChange={(e) => setResendEmail(e.target.value)}
                                        placeholder="your@email.com"
                                        sx={{ 
                                            mb: 2,
                                            '& .MuiOutlinedInput-root': {
                                                '&.Mui-focused fieldset': {
                                                    borderColor: theme.palette.warning.main,
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: theme.palette.warning.main,
                                                },
                                            }
                                        }}
                                        disabled={resending}
                                    />
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        onClick={handleResendVerification}
                                        disabled={resending}
                                        startIcon={resending ? <CircularProgress size={20} /> : <Send />}
                                        sx={{
                                            py: 1.5,
                                            mb: 2,
                                            borderRadius: 2,
                                            textTransform: 'none',
                                            background: `linear-gradient(135deg, ${theme.palette.warning.main} 0%, ${theme.palette.warning.dark} 100%)`,
                                            '&:hover': {
                                                background: `linear-gradient(135deg, ${theme.palette.warning.dark} 0%, ${theme.palette.warning.darker || '#B45309'} 100%)`,
                                            }
                                        }}
                                    >
                                        {resending ? 'Sending...' : 'Send Verification Email'}
                                    </Button>
                                    <Button
                                        fullWidth
                                        variant="text"
                                        onClick={() => setShowResendForm(false)}
                                        sx={{ 
                                            textTransform: 'none',
                                            color: theme.palette.warning.main,
                                            '&:hover': {
                                                backgroundColor: alpha(theme.palette.warning.main, 0.1),
                                            }
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                </Box>
                            )}
                        </Box>
                    </StyledPaper>
                </motion.div>
            </Box>
        );
    }

    return null;
};

export default UniversityHeadEmailVerification;
